import { Component, computed, inject } from '@angular/core';
import { TableComponent } from 'src/app/shared/table/components/table/table.component';
import {
  CalistActions,
  calistFeature,
  calistRenewFeature,
  selectCalistDeleting,
} from '../../calist.feature';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { ColDef } from 'ag-grid-enterprise';
import {
  ColumnFilter,
  DataRequest,
  GridSort,
} from 'src/app/shared/table/components/table/table.types';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { IconMenuRendererComponent } from 'src/app/shared/table/components/cell-renderers/icon-menu-renderer/icon-menu-renderer.component';
import { IconMenuConfig } from 'src/app/shared/table/components/cell-renderers/icon-menu-renderer/icon-menu-renderer.types';
import { DialogService } from 'src/app/shared/dialog/services/dialog/dialog.service';
import { DropdownFilterComponent } from 'src/app/shared/table/components/filters/dropdown-filter/dropdown-filter.component';
import {
  CACol,
  caExpirationStatus,
  caNearExpirationCategory,
  ExpirationStatus,
  NearExpirationCategory,
} from '../../calist.types';
import { DropdownFloatingFilterComponent } from 'src/app/shared/table/components/filters/dropdown-floating-filter/dropdown-floating-filter.component';
import { externalTextColumnFilter } from 'src/app/shared/table/components/filters/external-text-column-filter';
import { MatButton } from '@angular/material/button';
import { PageService } from 'src/app/shared/page/service/page.service';
import { Page } from 'src/app/shared/page/page.config';
import { CalistDataService } from 'src/app/shared/store/features/calist-data/calist-data.service';
import { map } from 'rxjs';
import { TotalComponent } from '../../../../shared/components/total/total.component';
import { CA } from 'src/app/shared/entities/ca';
import { UserSelector } from 'src/app/shared/store/user/user.feature';

@Component({
  selector: 'app-calist-main-table',
  imports: [TableComponent, SpinnerComponent, MatButton, TotalComponent],
  templateUrl: './main-table.component.html',
  styleUrl: './main-table.component.scss',
})
export class MainTableComponent {
  private langService = inject(LangService);
  private dialogService = inject(DialogService);
  private pageService = inject(PageService);
  private calistDataService = inject(CalistDataService);
  total = toSignal(
    this.calistDataService.data$.pipe(map(data => data?.length ?? 0)),
  );
  labels = this.langService.lang.features.calist;
  private lang = this.langService.lang;
  private store = inject(Store);
  isEditor = toSignal(this.store.select(UserSelector.selectIsEditor));
  private gridData$ = this.store.select(calistFeature.selectGridData);
  gridData = toSignal(this.gridData$);
  isDeleting = toSignal(this.store.select(selectCalistDeleting));
  renewing = toSignal(this.store.select(calistRenewFeature.selectRenewing));
  spinning = computed(
    () => this.gridData()?.loading || this.isDeleting() || !!this.renewing(),
  );

  private actionButtons: IconMenuConfig<CA> = {
    buttons: [
      {
        icon: 'edit',
        tooltip: this.labels.actions.edit.tooltip,
        callback: data => {
          if (data.caName) {
            this.store.dispatch(CalistActions.edit({ key: data.caName }));
          }
        },
      },
      {
        icon: 'delete',
        tooltip: this.labels.actions.delete.tooltip,
        callback: data => {
          this.dialogService.withConfirmation({
            text: this.labels.actions.delete.confirmation(data.caName ?? ''),
          })(() => this.store.dispatch(CalistActions.delete({ item: data })));
        },
      },
    ],
  };

  private formatExpireTime(ca: CA | undefined) {
    const expireTime = ca?.expireTime;
    return expireTime
      ? this.lang.shared.dateWithShortMonthName(new Date(expireTime))
      : '';
  }

  private statusColorClass: Record<ExpirationStatus, string> = {
    [ExpirationStatus.Active]: 'rag-green',
    [ExpirationStatus.Expired]: 'rag-red',
    [ExpirationStatus.Unknown]: '',
  };

  private nearXPCategoryColorClass: Record<NearExpirationCategory, string> = {
    [NearExpirationCategory.overOneYear]: 'rag-green',
    [NearExpirationCategory.oneYear]: 'rag-green',
    [NearExpirationCategory.sixMonths]: 'rag-amber',
    [NearExpirationCategory.expired]: 'rag-red',
  };

  private dataColumnDefs: ColDef<CA>[] = [
    {
      headerName: this.labels.headers.caName,
      field: 'caName',
      cellClass: ['link-color', 'curPointer'],
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: [externalTextColumnFilter],
      },
      onCellClicked: event => {
        const key = event.data?.caName;
        if (key) {
          this.store.dispatch(CalistActions.drilldown({ key }));
        }
      },
    },
    {
      colId: CACol.expiry,
      headerName: this.labels.headers.expiry,
      field: 'expireTime',
      maxWidth: 110,
      sortable: true,
      valueFormatter: params => this.formatExpireTime(params.data),
      filter: DropdownFilterComponent,
      floatingFilter: true,
      floatingFilterComponent: DropdownFloatingFilterComponent,
      floatingFilterComponentParams: {
        options: [
          NearExpirationCategory.expired,
          NearExpirationCategory.sixMonths,
          NearExpirationCategory.oneYear,
          NearExpirationCategory.overOneYear,
        ].map(value => ({
          value,
          label: this.labels.nearExpirationCategory[value],
        })),
      },
      cellClass: params => {
        const category = params.data
          ? caNearExpirationCategory(params.data)
          : null;
        return category ? this.nearXPCategoryColorClass[category] : undefined;
      },
    },
    {
      colId: CACol.status,
      headerName: this.labels.headers.status,
      sortable: true,
      filter: DropdownFilterComponent,
      floatingFilter: true,
      floatingFilterComponent: DropdownFloatingFilterComponent,
      floatingFilterComponentParams: {
        options: [ExpirationStatus.Active, ExpirationStatus.Expired].map(
          value => ({ value, label: this.labels.status[value] }),
        ),
      },
      valueGetter: params =>
        params.data ? this.labels.status[caExpirationStatus(params.data)] : '',
      cellClass: params => {
        const status = params.data ? caExpirationStatus(params.data) : null;
        return status
          ? ['text-center', this.statusColorClass[status]]
          : ['text-center'];
      },
      maxWidth: 100,
    },
  ];

  private actionColumnDef = {
    sortable: false,
    minWidth: 50,
    maxWidth: 120,
    cellRenderer: IconMenuRendererComponent,
    valueGetter: () => this.actionButtons,
  };

  private editorColumnDefs = [...this.dataColumnDefs, this.actionColumnDef];

  columnDefs = computed(() => {
    const isEditor = this.isEditor();
    return isEditor ? this.editorColumnDefs : this.dataColumnDefs;
  });

  onDataRequest(event: DataRequest) {
    this.store.dispatch(CalistActions.dataRequest(event));
  }

  onFilterChanged(event: ColumnFilter) {
    this.store.dispatch(CalistActions.applyFilter({ filter: event }));
  }

  onSortChanged(event: GridSort) {
    this.store.dispatch(CalistActions.applySorting({ sort: event }));
  }

  onCreate() {
    this.pageService.goToPage(Page.CreateCa);
  }
}
